import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { CDN, SMS_URL } from "../data";

const FILM = CDN.replace(/\/$/, "/") + "hf_20260830_190506_f353c393-9445-40a0-9a79-cd0febc90e7c.mp4";

const lineReveal = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.65, 0, 0.35, 1] },
  }),
};

export const Journey = () => {
  const journeyRef = useRef(null);
  const filmRef = useRef(null);
  const [idx, setIdx] = useState(0);
  const [reduced] = useState(() => window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  const [isMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);

  useEffect(() => {
    const film = filmRef.current;
    if (isMobile) {
      const p = film.play();
      if (p && p.catch) p.catch(() => {});
      return;
    }
    const journey = journeyRef.current;
    let duration = 0, target = 0, current = 0, raf;
    const onMeta = () => {
      duration = film.duration || 0;
      try { film.currentTime = 0.001; } catch (e) {}
    };
    film.addEventListener("loadedmetadata", onMeta);
    const prime = () => {
      const p = film.play();
      if (p && p.then) p.then(() => film.pause()).catch(() => {});
    };
    window.addEventListener("touchstart", prime, { once: true, passive: true });
    const progress = () => {
      const r = journey.getBoundingClientRect();
      const total = r.height - window.innerHeight;
      return Math.min(1, Math.max(0, -r.top / (total || 1)));
    };
    const tick = () => {
      current += (target - current) * 0.18;
      if (duration && Math.abs(film.currentTime - current * duration) > 0.02) {
        try { film.currentTime = current * duration; } catch (e) {}
      }
      raf = requestAnimationFrame(tick);
    };
    const update = () => {
      const p = progress();
      target = p;
      setIdx(Math.min(3, Math.floor(p * 4)));
    };
    if (!reduced) raf = requestAnimationFrame(tick);
    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", update);
      film.removeEventListener("loadedmetadata", onMeta);
    };
  }, [reduced, isMobile]);

  const jump = (i) => {
    const journey = journeyRef.current;
    const total = journey.offsetHeight - window.innerHeight;
    window.scrollTo({ top: journey.offsetTop + total * ((i + 0.5) / 4), behavior: "smooth" });
  };

  if (isMobile) {
    return (
      <section className="journey journey-mobile" id="journey" aria-label="Kaua'i property care">
        <div className="journey-stage">
          <video ref={filmRef} muted playsInline autoPlay loop preload="auto" crossOrigin="anonymous" poster={CDN + "hf_20260830_193346_8d0dd934-0cdc-4a3f-86c7-613ad6174ec0_min.webp"} src={FILM} />
          <div className="journey-scrim" aria-hidden="true" />
          <div className="m-hero" data-testid="mobile-hero">
            <p className="kicker">Lucho Gomez Connect</p>
            <h1 className="mask-lines">
              <span className="mask-line"><motion.span variants={lineReveal} initial="hidden" animate="show" custom={0}>Kaua'i,</motion.span></span>
              <span className="mask-line"><motion.span variants={lineReveal} initial="hidden" animate="show" custom={1}>cared for.</motion.span></span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}>
              Property care, land clearing, pond service, animal help and property management across Kaua'i — one local contact for all of it.
            </motion.p>
            <motion.div className="tags" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.95, duration: 0.6 }}>
              <span>Lihu'e</span><span>Kapa'a</span><span>All of Kaua'i</span>
            </motion.div>
            <motion.a
              className="m-hero-cta"
              href={SMS_URL}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              data-testid="mobile-hero-cta"
            >
              Text for a free estimate
            </motion.a>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="journey" id="journey" ref={journeyRef} aria-label="Kaua'i property care journey film">
      <div className="journey-stage">
        <video ref={filmRef} muted playsInline preload="auto" crossOrigin="anonymous" poster={CDN + "hf_20260830_193346_8d0dd934-0cdc-4a3f-86c7-613ad6174ec0_min.webp"} src={FILM} />
        <div className="journey-scrim" aria-hidden="true" />
        <div className="progress" aria-label="Journey chapters">
          {[0, 1, 2, 3].map((i) => (
            <button key={i} className={idx === i ? "on" : ""} onClick={() => jump(i)} aria-label={`Chapter ${i + 1}`} data-testid={`journey-dot-${i}`} />
          ))}
        </div>

        <article className={"chapter center" + (idx === 0 ? " is-active" : "")} data-testid="journey-chapter-0">
          <p className="kicker">Lucho Gomez Connect &mdash; Ch. 01</p>
          <h1 className="mask-lines">
            <span className="mask-line"><motion.span variants={lineReveal} initial="hidden" animate="show" custom={0}>Kaua'i,</motion.span></span>
            <span className="mask-line"><motion.span variants={lineReveal} initial="hidden" animate="show" custom={1}>cared for.</motion.span></span>
          </h1>
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75, duration: 0.7 }}>
            Property care, land clearing, pond service, animal help and property management across Kaua'i — one local contact for all of it.
          </motion.p>
          <motion.div className="tags" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1, duration: 0.7 }}>
            <span>Lihu'e</span><span>Kapa'a</span><span>All of Kaua'i</span>
          </motion.div>
        </article>

        <article className={"chapter" + (idx === 1 ? " is-active" : "")} data-testid="journey-chapter-1">
          <p className="kicker">Ch. 02 &mdash; Land</p>
          <h2>Overgrown? Under control.</h2>
          <p>Guinea grass clearing, yard cleanups, hauling and storm recovery. Big job or small, it gets handled.</p>
        </article>

        <article className={"chapter" + (idx === 2 ? " is-active" : "")} data-testid="journey-chapter-2">
          <p className="kicker">Ch. 03 &mdash; The promise</p>
          <h2>One local contact. Many solutions.</h2>
          <p>Twelve service lines — land care, animal and farm help, pond service, fences, pressure washing, and eyes on the house while you are away.</p>
        </article>

        <article className={"chapter" + (idx === 3 ? " is-active" : "")} data-testid="journey-chapter-3">
          <p className="kicker">Ch. 04 &mdash; Local. Reliable.</p>
          <h2>Done right, every time.</h2>
          <p>Every property treated like his own. That is the whole promise.</p>
          <a className="journey-cta" href={SMS_URL} data-testid="journey-estimate-cta">
            <span>Text for a free estimate</span>
            <span className="journey-cta-trail" aria-hidden="true"></span>
          </a>
        </article>
      </div>
    </section>
  );
};
